import is from '@sindresorhus/is';
import { first } from 'lodash';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';

import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';

// Deep-import (not via the chat barrel) so this feature module stays free of
// a chat-barrel <-> feature import cycle.
import { useChatComposerCallbacks } from '../../chat/chat-composer-contexts';
import { useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { getNewerVersions, hasReviewedOrPublishedVersion } from './notification-bar/notification-version-utils';
import { useReviewerDisplay } from './prompts/use-reviewer-display';
import type { PromptKind } from './resolve-prompt-kind';
import { resolvePromptKind } from './resolve-prompt-kind';

type ItemType = 'job-level' | 'job-role';

type JobProfileIdleBadgeProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly version?: number;
};

type BadgeKind = 'review' | 'publish' | 'assign';

const BADGE_VARIANT: Record<BadgeKind, 'warning' | 'info' | 'success'> = {
    review: 'info',
    publish: 'success',
    assign: 'warning',
};

const BADGE_TRANSLATION_KEY = {
    review: 'pages.job-architecture-v2.prompts.idle-review',
    publish: 'pages.job-architecture-v2.prompts.idle-publish',
    assign: 'pages.job-architecture-v2.prompts.idle-assign',
} as const;

// Maps prompt state into the badge shown in the chat's idle slot. Mirrors the
// branching in RoleReviewPrompt: `role-review` only surfaces "Review" when the
// current user is the reviewer; otherwise the only available action is
// assigning oneself, so we show "Assign".
const resolveBadgeKind = (kind: PromptKind, isReviewerCurrentUser: boolean): BadgeKind | null => {
    switch (kind) {
        case 'publish':
            return 'publish';
        case 'assign-reviewer':
            return 'assign';
        case 'role-review':
            return isReviewerCurrentUser ? 'review' : 'assign';
        default:
            return null;
    }
};

export const JobProfileIdleBadge: FunctionComponent<JobProfileIdleBadgeProps> = ({
    architectureId,
    itemId,
    itemType,
    version,
}) => {
    const translate = useTranslate();
    const isJobArchitect = useHasRole('job_architect');
    const { expandCollapsedComposer } = useChatComposerCallbacks();

    const { data: versions, isLoading: isLoadingVersions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const activeVersion = is.number(version) ? version.toString() : first(versions)?.version.toString();

    const { data: item } = useJobItemDetails({
        architectureId,
        enabled: !isLoadingVersions,
        itemId,
        itemType,
        version: activeVersion,
    });

    const isStaleVersion = useMemo(() => {
        const newerVersions = getNewerVersions(versions, item?.versionInfo?.version);
        return hasReviewedOrPublishedVersion(newerVersions);
    }, [versions, item?.versionInfo?.version]);

    const { isReviewerCurrentUser } = useReviewerDisplay(item ?? { reviewer: undefined });

    const promptKind = useMemo<PromptKind>(() => resolvePromptKind(item, isJobArchitect), [item, isJobArchitect]);
    const badgeKind = resolveBadgeKind(promptKind, isReviewerCurrentUser);

    if (item == null || isStaleVersion || badgeKind === null) {
        return null;
    }

    return (
        <Badge variant={BADGE_VARIANT[badgeKind]} onClick={expandCollapsedComposer}>
            {translate(BADGE_TRANSLATION_KEY[badgeKind])}
        </Badge>
    );
};
